import React, { useEffect, useMemo, useState } from "react";
import { API } from "../utils/api";
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Users,
  TrendingUp,
  RefreshCw,
  Loader2,
  UserCheck,
  AlertCircle,
  Gift,
  PieChart,
  DollarSign,
  Clock,
} from "lucide-react";

export default function AdminStats() {
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdraws: 0,
    totalBalance: 0,
    totalclient: 0,
    activeClients: 0,
    totalCommissionsPaid: 0,
    commissionCount: 0,
    commissionSplit: { signup: 0, tx: 0 },
    chartData: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");

  const loadStats = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/admin/stats");
      setStats(res.data || {});
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      setError("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const growthRate = useMemo(() => {
    const data = stats.chartData || [];
    if (data.length < 2) return 0;
    const last = Number(data[data.length - 1]?.deposits || 0);
    const prev = Number(data[data.length - 2]?.deposits || 0);
    if (prev === 0) return last > 0 ? 100 : 0;
    return Math.round(((last - prev) / prev) * 100);
  }, [stats.chartData]);

  // Animated Counter
  const AnimatedCounter = ({ value, prefix = "", suffix = "", money = false }) => {
    const [displayValue, setDisplayValue] = useState(0);
    useEffect(() => {
      const end = parseFloat(value) || 0;
      const duration = 800;
      const steps = 40;
      const increment = end / steps;
      let current = 0;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          setDisplayValue(end);
          clearInterval(timer);
        } else {
          setDisplayValue(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }, [value]);

    return (
      <span className="font-bold text-3xl md:text-4xl flex items-center gap-1">
        {money && <DollarSign className="w-7 h-7 text-gray-800" />}
        {prefix}
        {Math.round(displayValue).toLocaleString()}
        {suffix}
      </span>
    );
  };

  const statCards = [
    {
      title: "Total Deposits",
      value: stats.totalDeposits || 0,
      icon: <ArrowUpRight className="w-8 h-8" />,
      color: "from-emerald-600 to-green-700",
      text: "text-emerald-700",
      money: true,
    },
    {
      title: "Total Withdrawals",
      value: stats.totalWithdraws || 0,
      icon: <ArrowDownRight className="w-8 h-8" />,
      color: "from-red-600 to-rose-700",
      text: "text-red-700",
      money: true,
    },
    {
      title: "Total Balance",
      value: stats.totalBalance || 0,
      icon: <Wallet className="w-8 h-8" />,
      color: "from-gray-700 to-gray-900",
      text: "text-gray-800",
      money: true,
    },
    {
      title: "Total Clients",
      value: stats.totalclient || 0,
      icon: <Users className="w-8 h-8" />,
      color: "from-blue-600 to-indigo-700",
      text: "text-blue-700",
    },
    {
      title: "Active Clients",
      value: stats.activeClients || 0,
      icon: <UserCheck className="w-8 h-8" />,
      color: "from-teal-600 to-emerald-700",
      text: "text-teal-700",
    },
    {
      title: "Growth Rate (Deposits d/d)",
      value: growthRate,
      icon: <TrendingUp className="w-8 h-8" />,
      color: growthRate >= 0 ? "from-green-600 to-emerald-700" : "from-red-600 to-rose-700",
      text: growthRate >= 0 ? "text-green-700" : "text-red-700",
      suffix: "%",
    },
    {
      title: "Total Commissions",
      value: stats.totalCommissionsPaid || 0,
      icon: <Gift className="w-8 h-8" />,
      color: "from-indigo-600 to-purple-700",
      text: "text-indigo-700",
      money: true,
    },
  ];

  const safePct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Dark Header with Logo */}
      <div className="w-full bg-gray-100 shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-6">

          {/* Title Section */}
          <div>
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              System Statistics
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Real-time overview of platform performance
            </p>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">

            {/* Gray Refresh Button */}
            <button
              onClick={loadStats}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 
                   bg-gray-200 text-gray-800 
                   rounded-xl hover:bg-gray-300 
                   transition-all shadow-md disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              {loading ? "Refreshing..." : "Refresh"}
            </button>

            {/* Last Updated */}
            {lastUpdated && (
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{lastUpdated}</span>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="flex-1 p-6 md:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3 mb-8 shadow-sm">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          {/* Loading Skeleton */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(7)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl shadow-xl p-8 border border-gray-200 animate-pulse"
                >
                  <div className="h-8 bg-gray-200 rounded-lg w-3/4 mb-4"></div>
                  <div className="h-12 bg-gray-200 rounded-xl w-full"></div>
                </div>
              ))}
            </div>
          )}

          {/* Stats Grid */}
          {!loading && !error && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {statCards.map((card, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-3xl shadow-xl p-8 border border border-gray-200 hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className={`p-4 rounded-2xl bg-gradient-to-br ${card.color} text-white shadow-lg`}>
                        {card.icon}
                      </div>
                      {card.suffix && (
                        <div className={`text-2xl font-bold ${card.text} flex items-center`}>
                          {card.value >= 0 ? "+" : ""}
                          {card.value}{card.suffix}
                        </div>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm font-medium mb-3">
                      {card.title}
                    </p>

                    <AnimatedCounter
                      value={card.value}
                      money={card.money}
                      suffix={card.suffix || ""}
                    />
                  </div>
                ))}
              </div>

              {/* Bottom Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Commission Split */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                      <PieChart className="w-7 h-7 text-gray-700" />
                      Commission Split
                    </h3>
                    <div className="text-right text-sm text-gray-600">
                      <p className="font-semibold">${(stats.totalCommissionsPaid || 0).toLocaleString()}</p>
                      <p>{stats.commissionCount || 0} total payouts</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 text-center border border-indigo-200">
                      <p className="text-sm text-gray-600 mb-2">Signup Bonuses</p>
                      <p className="text-2xl font-bold text-indigo-700 flex items-center justify-center gap-1">
                        <DollarSign className="w-6 h-6" />
                        {(stats.commissionSplit?.signup || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {safePct(stats.commissionSplit?.signup || 0, stats.totalCommissionsPaid || 0)}% of total
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 text-center border border-purple-200">
                      <p className="text-sm text-gray-600 mb-2">Per-Transaction</p>
                      <p className="text-2xl font-bold text-purple-700 flex items-center justify-center gap-1">
                        <DollarSign className="w-6 h-6" />
                        {(stats.commissionSplit?.tx || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {safePct(stats.commissionSplit?.tx || 0, stats.totalCommissionsPaid || 0)}% of total
                      </p>
                    </div>
                  </div>
                </div>

                {/* Platform Summary */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Platform Summary</h3>
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-3xl font-bold text-emerald-600 flex items-center justify-center gap-1">
                        <DollarSign className="w-7 h-7" />
                        {((stats.totalDeposits || 0) - (stats.totalWithdraws || 0)).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">Net Flow</p>
                    </div>

                    <div>
                      <p className="text-3xl font-bold text-blue-600">
                        {stats.totalclient > 0
                          ? Math.round(((stats.activeClients || 0) / stats.totalclient) * 100)
                          : 0}%
                      </p>
                      <p className="text-sm text-gray-600 mt-2">Active Rate</p>
                    </div>

                    <div>
                      <p className="text-3xl font-bold text-gray-800 flex items-center justify-center gap-1">
                        <DollarSign className="w-7 h-7" />
                        {stats.totalclient > 0
                          ? Math.round((stats.totalBalance || 0) / stats.totalclient).toLocaleString()
                          : 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">Avg. Balance / Client</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}